/** One mounted activity's canvas, drawn from the scene it ships and from nothing this app knows.
 *
 * There is no branch on the activity id here and there must never be one again. The renderer's
 * interact half draws whatever the document says, the manifest decides which canvases this person
 * reaches, and a bundle the platform has never heard of gets the same code path as a first-party
 * one. That is the plugin contract; `surfaceOf`, the map this replaced, was its counterexample.
 */

import {
  ControlSurface,
  controlEntityIndex,
  type PressAction,
} from "@nebulize/overlay/interact";
import { useCallback, useEffect, useMemo } from "react";

import type { CanvasStreamOptions } from "../../lib/canvasStream";
import { submit } from "../../lib/runtime";
import { useCanvasState } from "../../lib/useCanvasState";
import { CanvasPicture } from "../canvas/CanvasPicture";
import { canvasLayout, controlLayerOf, documentsDraw } from "../canvas/composition";

export function MountSurface({
  mountId,
  activityId,
  stream,
  only,
  quiet = false,
  onPresence,
}: {
  mountId: string;
  activityId: string;
  /** A test seam for an injected socket factory, the same one useCanvasState takes. */
  stream?: CanvasStreamOptions;
  /** Narrow to one of the kinds this connection already reaches. Absent draws all of them. */
  only?: string;
  /** Render nothing at all when this mount draws nothing here, rather than saying so. */
  quiet?: boolean;
  /** Told, once the state loads, whether this mount draws anything here. */
  onPresence?: (draws: boolean) => void;
}) {
  const { state, error, waitingPosition, frame, cue } = useCanvasState(mountId, stream, only);

  /** No declaration reaches this host, and `canvasLayout` answers that with the flowed rule. */
  const layout = canvasLayout(undefined);

  const entities = useMemo(() => controlEntityIndex(state?.entities ?? []), [state]);
  const held = useMemo(() => state?.scenes ?? [], [state]);
  const documents = useMemo(
    () => (only === undefined ? held : held.filter((one) => one.canvas_kind === only)),
    [held, only],
  );
  const reached = useMemo(() => state?.canvases ?? [], [state]);
  const canvasKinds = useMemo(
    () => (only === undefined ? reached : reached.filter((kind) => kind === only)),
    [reached, only],
  );
  const regions = useMemo(() => state?.regions ?? [], [state]);

  /** The one submission path. The interact half holds no client, so the host hands it this.
   *
   * The press time is the interact half's, passed through untouched and never invented here.
   */
  const press = useCallback(
    (action: PressAction) => submit(mountId, action.submission, action.value, action),
    [mountId],
  );

  const loaded = state !== null;
  const draws = useMemo(() => documentsDraw(documents, layout), [documents, layout]);
  useEffect(() => {
    if (loaded) {
      onPresence?.(draws);
      return;
    }
    // Queued is pending, not settled: the socket is open and the place is kept, so it resolves
    // into a welcome rather than reporting anything here. A host waiting on every mount's presence
    // owns its own grace period for a queue that takes a while, rather than this one guessing.
    if (waitingPosition !== null) return;
    // A socket that surfaced an error before ever reaching a state has settled into drawing
    // nothing here, the same as an empty document list once loaded: a mount that never connects
    // must not hold a host waiting on it forever.
    if (error !== null) onPresence?.(false);
  }, [loaded, draws, waitingPosition, error, onPresence]);

  if (waitingPosition !== null) {
    if (quiet) return null;
    return <Note text={`waiting to get in, number ${String(waitingPosition)}`} />;
  }
  if (state === null) return quiet ? null : <Note text={error ?? `opening ${activityId}`} />;
  if (!draws) {
    // Never silently blank: nothing drawn and nothing said reads exactly like being filtered out.
    // A host that lists what is here separately says `quiet` and takes that job on itself.
    if (quiet) return null;
    return <Note text={`${activityId} draws nothing on ${canvasKinds.join(", ") || "this canvas"}`} />;
  }

  return (
    <section aria-label={activityId}>
      {error !== null && <Note text={error} />}
      <ControlSurface
        documents={documents}
        entities={entities}
        canvasKinds={canvasKinds}
        submit={press}
        layer={controlLayerOf(layout)}
        {...(frame === undefined ? {} : { frame })}
      />
      <CanvasPicture
        layout={layout}
        canvasKind={only ?? canvasKinds[0] ?? ""}
        scenes={documents}
        entities={entities}
        regions={regions}
        stale={error !== null}
        {...(cue === undefined ? {} : { cue })}
        {...(state.arrangement === undefined ? {} : { arrangement: state.arrangement })}
        onPick={(picked) => {
          // A drawn node with an action is pressable, exactly as a control is.
          if (picked.action !== undefined) {
            void submit(mountId, picked.action.submission, picked.action.value);
          }
        }}
      />
    </section>
  );
}

function Note({ text }: { text: string }) {
  return (
    <p
      style={{
        margin: 0,
        padding: "12px 16px",
        color: "rgba(255,255,255,0.55)",
        font: "13px ui-monospace, monospace",
      }}
    >
      {text}
    </p>
  );
}
